// IndexedDB 封装 - 替代 localStorage，支持大容量图片存储
const DB_NAME = 'hypwr_website_db';
const DB_VERSION = 8; // 添加图片索引存储表

interface DBConfig {
  name: string;
  keyPath: string;
}

// 数据表配置
const STORES: Record<string, DBConfig> = {
  categories: { name: 'categories', keyPath: 'id' },
  products: { name: 'products', keyPath: 'id' },
  inquiries: { name: 'inquiries', keyPath: 'id' },
  news: { name: 'news', keyPath: 'id' },
  applications: { name: 'applications', keyPath: 'id' },
  params: { name: 'params', keyPath: 'key' },
  jobs: { name: 'jobs', keyPath: 'id' },
  honors: { name: 'honors', keyPath: 'id' },
  pageContent: { name: 'pageContent', keyPath: 'key' },
  systemConfig: { name: 'systemConfig', keyPath: 'key' },
  attachments: { name: 'attachments', keyPath: 'id' },
  operationLogs: { name: 'operationLogs', keyPath: 'id' },
  jobApplications: { name: 'jobApplications', keyPath: 'id' },
  images: { name: 'images', keyPath: 'key' },
  imageIndex: { name: 'imageIndex', keyPath: 'key' },
};

class IndexedDBService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  // 初始化数据库
  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    
    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB 打开失败');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB 初始化成功');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = (event as IDBVersionChangeEvent).oldVersion;
        
        // 如果是升级（而非首次创建），删除旧存储以清理损坏数据
        if (oldVersion > 0) {
          Object.values(STORES).forEach(config => {
            if (db.objectStoreNames.contains(config.name)) {
              db.deleteObjectStore(config.name);
            }
          });
        }
        
        // 创建所有对象存储
        Object.values(STORES).forEach(config => {
          if (!db.objectStoreNames.contains(config.name)) {
            db.createObjectStore(config.name, { keyPath: config.keyPath });
          }
        });
      };
    });

    return this.initPromise;
  }

  // 确保数据库已初始化
  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('IndexedDB 未初始化');
    }
    return this.db;
  }

  // 获取数据
  async get<T>(storeName: string, key: string): Promise<T | null> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get(key);

        request.onsuccess = () => {
          const config = Object.values(STORES).find(s => s.name === storeName);
          // keyPath='key' 的存储使用 {key, value} 结构
          // keyPath='id' 的存储直接存对象
          if (request.result) {
            resolve(config?.keyPath === 'key' ? request.result.value : request.result);
          } else {
            resolve(null);
          }
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn(`IndexedDB get 失败 (${storeName}):`, error);
      return null;
    }
  }

  // 获取所有数据
  async getAll<T>(storeName: string): Promise<T[]> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();

        request.onsuccess = () => {
          const config = Object.values(STORES).find(s => s.name === storeName);
          // keyPath='key' 的存储使用 {key, value} 结构
          // keyPath='id' 的存储直接存对象
          const results = request.result.map(item => 
            config?.keyPath === 'key' ? item.value : item
          );
          resolve(results);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn(`IndexedDB getAll 失败 (${storeName}):`, error);
      return [];
    }
  }

  // 保存数据
  async set<T>(storeName: string, key: string, value: T): Promise<void> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        
        // 根据 store 配置决定 keyPath
        const config = Object.values(STORES).find(s => s.name === storeName);
        let data: any;
        
        if (config?.keyPath === 'key') {
          // keyPath='key' 的存储使用 {key, value} 结构
          data = { key, value };
        } else {
          // keyPath='id' 的存储直接存对象，确保包含 id 字段
          const valueWithId = value as any;
          if (typeof valueWithId === 'object' && valueWithId !== null) {
            data = { ...valueWithId, [config?.keyPath || 'id']: key };
          } else {
            // 如果不是对象，包装一下
            data = { [config?.keyPath || 'id']: key, value };
          }
        }
        
        console.log(`[IndexedDB] 保存到 ${storeName}, key=${key}, 数据类型=${typeof data}`);
        
        const request = store.put(data);

        request.onsuccess = () => {
          console.log(`[IndexedDB] 保存成功: ${storeName}/${key}`);
          resolve();
        };
        request.onerror = () => {
          console.error(`[IndexedDB] 保存失败: ${storeName}/${key}`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.warn(`IndexedDB set 失败 (${storeName}):`, error);
      throw error;
    }
  }

  // 删除数据
  async delete(storeName: string, key: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.delete(key);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn(`IndexedDB delete 失败 (${storeName}):`, error);
    }
  }

  // 清空表
  async clear(storeName: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn(`IndexedDB clear 失败 (${storeName}):`, error);
    }
  }
}

// 单例实例
const idbService = new IndexedDBService();

// 兼容层：提供与原来 localStorage 相同的 API
export const storage = {
  // 从 localStorage 迁移数据到 IndexedDB
  async migrateFromLocalStorage(key: string, storeName: string): Promise<boolean> {
    try {
      const localData = localStorage.getItem(key);
      if (localData) {
        await idbService.set(storeName, 'default', JSON.parse(localData));
        console.log(`已迁移 ${key} 到 IndexedDB`);
        return true;
      }
    } catch (error) {
      console.warn(`迁移 ${key} 失败:`, error);
    }
    return false;
  },

  // 获取数据（优先 IndexedDB，回退 localStorage）
  async getItem<T>(key: string, storeName: string): Promise<T | null> {
    // 先尝试 IndexedDB
    const idbData = await idbService.get<T>(storeName, 'default');
    if (idbData !== null) {
      return idbData;
    }
    
    // 回退到 localStorage 并迁移
    const localData = localStorage.getItem(key);
    if (localData) {
      try {
        const parsed = JSON.parse(localData);
        // 异步迁移，不阻塞
        idbService.set(storeName, 'default', parsed).catch(console.error);
        return parsed;
      } catch {
        return null;
      }
    }
    return null;
  },

  // 保存数据（仅使用 IndexedDB，localStorage 作为可选迁移源）
  async setItem<T>(key: string, storeName: string, value: T): Promise<void> {
    // 保存到 IndexedDB（主要存储）
    await idbService.set(storeName, 'default', value);
    
    // 不再自动备份到 localStorage，避免大图片导致 QuotaExceededError
    // localStorage 仅用于数据迁移来源
  },

  // 删除数据
  async removeItem(key: string, storeName: string): Promise<void> {
    await idbService.delete(storeName, 'default');
    localStorage.removeItem(key);
  },

  // 列表操作（用于数组数据）
  async getList<T>(storeName: string): Promise<T[]> {
    return idbService.getAll<T>(storeName);
  },

  async saveList<T>(key: string, storeName: string, list: T[]): Promise<void> {
    // 清空并重新保存所有数据
    await idbService.clear(storeName);
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      const id = (item as any).id || `${Date.now()}_${i}`;
      await idbService.set(storeName, id, item);
    }
    
    // 不再自动备份到 localStorage，避免大图片导致 QuotaExceededError
    // localStorage 仅用于数据迁移来源
  },
};

export default idbService;
